import {
  Autocomplete,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import InputHeader from '../../../components/InputHeader';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { CrewMember, NewCrewMember } from '../../../types/crew';
import { Operator } from '../../../types/operator';
import { CrewFormValues } from './types';
import { useCurrentUser } from '../../../contexts/currentUser';
import useIotsAdminForm from '../../../hooks/useIotsAdminForm';
import { CommonMainPanelForm } from '../../../components/AdminTemplates';
import { CrewMemberRole } from '../../../types/core';

export interface CrewFormProps {
  onSubmit: (values: NewCrewMember, callback: () => void) => void;
  onCancel?: () => void;
  onCrewMemberNameChange: (name: string) => void;
  isLoading: boolean;
  crewMember?: CrewMember | null;
  disabled?: boolean;
}

const CrewForm = ({
  onSubmit,
  onCancel,
  onCrewMemberNameChange,
  isLoading,
  disabled = false,
  crewMember = null,
}: CrewFormProps) => {
  const { t } = useLanguage();
  const { getOperators } = useIotsApi();
  const { isOperator, userOperator } = useCurrentUser();

  const {
    data: operators = [],
    isFetching: isFetchingOperators,
    error: operatorsError,
  } = useQuery({
    queryKey: ['operators'],
    queryFn: getOperators,
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    // Default sorting by name
    select: operators =>
      [...operators].sort((a, b) => a.name.localeCompare(b.name)),
  });

  useQueryErrorNotifier([operatorsError]);

  const isOperatorAutocompleteDisabled =
    isFetchingOperators || isOperator || !!crewMember;

  const areInputsDisabled = disabled || isLoading;

  const initialValues = useMemo<CrewFormValues>(() => {
    if (!crewMember) {
      return {
        operatorId: userOperator?.operatorId ?? '',
        firstName: '',
        lastName: '',
        role: '',
      };
    }
    return {
      operatorId: crewMember.operatorId,
      firstName: crewMember.firstName,
      lastName: crewMember.lastName,
      role: crewMember.crewMemberRole,
    };
  }, [crewMember, userOperator]);

  const {
    formState,
    materialInputHelpers: {
      getBaseProps,
      getInputProps,
      getTextFieldProps,
      getSelectProps,
      renderError,
    },
  } = useIotsAdminForm(
    {
      isEditingByDefault: !crewMember,
      initialValues,
      onEditionCancel: onCancel,
      onSubmit: (values, callback) => {
        onSubmit(
          {
            operatorId: values.operatorId as string,
            firstName: values.firstName,
            lastName: values.lastName,
            crewMemberRole: values.role as CrewMemberRole,
          },
          callback,
        );
      },
      validate: (values, setError) => {
        if (!values.operatorId) {
          setError('operatorId', t('validation.invalid-option'));
        }
        if (!values.firstName.trim()) {
          setError('firstName', t('validation.required-field'));
        }
        if (!values.lastName) {
          setError('lastName', t('validation.required-field'));
        }
        if (!values.role) {
          setError('role', t('validation.required-field'));
        }
      },
      resetOnInitialValuesChange: () =>
        Boolean(crewMember || userOperator?.operatorId),
    },
    { shouldInputsBeDisabled: areInputsDisabled },
  );

  const {
    isEditing,
    handleSubmit,
    cancelEdition,
    requestEdition,
    hasVisibleErrors,
    haveValuesChanged,
    values,
    updateValues,
    touchField,
  } = formState;

  useEffect(() => {
    if (values.firstName || values.lastName) {
      onCrewMemberNameChange(
        [values.firstName, values.lastName].filter(Boolean).join(' '),
      );
    }
  }, [onCrewMemberNameChange, values.firstName, values.lastName]);

  return (
    <CommonMainPanelForm
      onSubmit={handleSubmit}
      onCancelEdition={cancelEdition}
      onEditionRequest={requestEdition}
      isEditing={isEditing}
      isSubmitDisabled={
        areInputsDisabled || !haveValuesChanged || hasVisibleErrors
      }
      isSubmitting={isLoading}
    >
      <div>
        {!isOperator && (
          <label className="block mb-4">
            <InputHeader required>{t('content.operator')}</InputHeader>
            <FormControl error={getInputProps('operatorId').error}>
              <Autocomplete<Operator>
                value={
                  operators.find(({ id }) => id === values.operatorId) || null
                }
                onChange={(_event, selectedOption) =>
                  updateValues({ operatorId: selectedOption?.id ?? '' }, true)
                }
                options={operators}
                onBlur={() => touchField('operatorId')}
                loading={isFetchingOperators}
                disabled={disabled || isOperatorAutocompleteDisabled}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={getTextFieldProps('operatorId').error}
                    placeholder="Select..."
                  />
                )}
                size="small"
                getOptionLabel={operator =>
                  `${operator.name}${operator.isActive ? '' : ` (${t('content.inactive')})`}`
                }
                getOptionDisabled={option => !option.isActive}
              />
              {renderError('operatorId')}
            </FormControl>
          </label>
        )}
        <label className="block mb-4">
          <InputHeader required>{t('content.first-name')}</InputHeader>
          <TextField
            placeholder={t('content.first-name')}
            {...getTextFieldProps('firstName')}
          />
        </label>
        <label className="block mb-4">
          <InputHeader required>{t('content.last-name')}</InputHeader>
          <TextField
            placeholder={t('content.last-name')}
            {...getTextFieldProps('lastName')}
          />
        </label>
        <label className="block mb-4">
          <InputHeader required>{t('content.crew-role')}</InputHeader>
          <FormControl error={getBaseProps('role').error}>
            <Select
              {...getSelectProps('role')}
              displayEmpty
              disabled={getBaseProps('role').disabled || !!crewMember}
              renderValue={selected => {
                if (!selected) {
                  return (
                    <span className="text-iots-grey-400">
                      {t('content.select')}
                    </span>
                  );
                }
                return t(`crew-member-roles.${selected}`);
              }}
            >
              <MenuItem value={CrewMemberRole.Skipper}>
                {t(`crew-member-roles.${CrewMemberRole.Skipper}`)}
              </MenuItem>
              <MenuItem value={CrewMemberRole.Crew}>
                {t(`crew-member-roles.${CrewMemberRole.Crew}`)}
              </MenuItem>
            </Select>
            {renderError('role')}
          </FormControl>
        </label>
      </div>
    </CommonMainPanelForm>
  );
};

export default CrewForm;
